/**
 * What the run is doing while it generates.
 *
 * Generation takes several minutes — the worker extracts every document, then runs the modeling
 * methodology over them. A static "working on it…" line across that span reads as a hung page, and the
 * only recourse a reviewer has is to reload and hope.
 *
 * So this reports the real pipeline. `queued` / `running` / `worker_returned` are genuine states the
 * run moves through, not a decorative bar: the worker sets them as it claims the job and deposits its
 * output. Nothing here is faked or interpolated — if a stage is marked done, it is done.
 */

import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { DraftRunStatus } from "../../store/queries/contractModelDrafts";
import {
  GENERATION_STAGES,
  formatElapsed,
  generationStageIndex,
} from "./utils";

const Card = styled.div`
  display: flex;
  gap: 1.25rem;
  align-items: flex-start;
  padding: 1.25rem;
  border: 1px solid var(--surface-border);
  border-radius: 6px;
  background: var(--surface-card);
`;

const Stages = styled.ol`
  list-style: none;
  margin: 0.75rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Stage = styled.li<{ $state: "done" | "current" | "todo" }>`
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  color: ${({ $state }) =>
    $state === "todo" ? "var(--text-color-secondary)" : "var(--text-color)"};
  font-weight: ${({ $state }) => ($state === "current" ? 600 : 400)};

  /* A todo stage is dimmed rather than hidden — seeing what is still to come is the point. */
  opacity: ${({ $state }) => ($state === "todo" ? 0.55 : 1)};
`;

const StageDetail = styled.p`
  margin: 0.15rem 0 0;
  font-size: 0.85rem;
  font-weight: 400;
  color: var(--text-color-secondary);
`;

type StageState = "done" | "current" | "todo";

const stageStateAt = (index: number, activeIndex: number): StageState => {
  if (index < activeIndex) return "done";
  if (index === activeIndex) return "current";
  return "todo";
};

const STAGE_ICON: Record<StageState, string> = {
  done: "pi pi-check-circle text-green-600",
  current: "pi pi-spin pi-spinner text-primary",
  todo: "pi pi-circle text-color-secondary",
};

interface IGenerationProgressProps {
  status: DraftRunStatus;
  /** When the run last changed — used for "in this step for Nm". */
  updatedAt: string;
  documentCount: number;
}

function GenerationProgress({
  status,
  updatedAt,
  documentCount,
}: IGenerationProgressProps) {
  // Re-render once a minute so the elapsed figure doesn't go stale on a page left open.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const activeIndex = generationStageIndex(status);
  if (activeIndex === -1) return null;

  const elapsed = formatElapsed(updatedAt, now);
  const active = GENERATION_STAGES[activeIndex];

  return (
    <Card>
      <ProgressSpinner
        style={{ width: "2.5rem", height: "2.5rem", margin: 0 }}
        strokeWidth="4"
        aria-label="Generating"
      />
      <div className="flex-1">
        <div className="flex align-items-baseline gap-2 flex-wrap">
          <span className="font-semibold text-lg">{active.label}</span>
          {elapsed ? (
            <span className="text-color-secondary text-sm">
              in this step for {elapsed}
            </span>
          ) : null}
        </div>
        <p className="text-color-secondary text-sm mt-1 mb-0">
          Reading {documentCount}{" "}
          {documentCount === 1 ? "document" : "documents"}. This usually takes a
          few minutes — you can leave this page and come back.
        </p>

        <Stages>
          {GENERATION_STAGES.map((stage, i) => {
            const state = stageStateAt(i, activeIndex);
            return (
              <Stage key={stage.status} $state={state}>
                <i className={STAGE_ICON[state]} />
                <span>
                  {stage.label}
                  {state === "current" ? (
                    <StageDetail>{stage.detail}</StageDetail>
                  ) : null}
                </span>
              </Stage>
            );
          })}
        </Stages>
      </div>
    </Card>
  );
}

export default GenerationProgress;
